import React from 'react';
import { get, map, fromPairs, cloneDeep, set, find, every, filter, includes, reduce, isEmpty, noop, isEqual } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

// local dependencies
import { authFlowMemberVerificationMatchCriteria as AuthFlowMemberVerificationMatchCriteriaView } from 'ui/views/widgets/authFlowMemberVerificationMatchCriteria';
import { AuthFlowMemberVerificationMatchCriteriaService } from 'services/authFlowMemberVerificationMatchCriteria';
import { SERVICE_RESPONSE_STATUS, PAGE_LEVEL_PRELOADER_TEMPLATES } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { updateMemberVerificationState } from 'store/actions/updateMemberVerificationState';

import { REDUCER } from 'constants/entity-names';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import queryString from 'query-string';
import { getStore } from 'store';

// get a global redux store ( creates empty store if not already created )
const store = getStore();

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowMemberVerificationMatchCriteriaWidget } from 'ui/containers/AuthFlowMemberVerificationMatchCriteriaWidget';
 *
 * // render
 * <AuthFlowMemberVerificationMatchCriteriaWidget propName='propValue'/>
 */
export class _AuthFlowMemberVerificationMatchCriteriaWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods to instance
        this.handleSetChoice = this.handleSetChoice.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            fieldsStatus: {},
            criteriaStatus: {
                total: false,
                mandatory: false,
                optional: false,
            },
        };

        // create service instance
        this.authFlowMemberVerificationMatchCriteriaService = new AuthFlowMemberVerificationMatchCriteriaService();
        
        // start component data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowMemberVerificationMatchCriteriaWidget.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowMemberVerificationMatchCriteriaService.cancel();

        // get status and service data
        const { status, data } = await this.authFlowMemberVerificationMatchCriteriaService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                context: {
                    memberId: this.props.memberId,
                    patientId: this.props.patientId
                }
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            // check if the user verification status, is present in the store
            const memberVerificationState = store.getState()[ REDUCER.MEMBER_VERIFICATION_STATE ];

            let fieldsStatus = fromPairs( map( data.criteria, item => {
                return [ item.name, { result: null, value: null, } ];
            } ) );

            if( ! isEmpty( memberVerificationState ) ) {
                if( ! isEmpty( get( memberVerificationState, 'fieldsStatus', {} ) ) ) {
                    fieldsStatus = get( memberVerificationState, 'fieldsStatus' );

                    // clear the redux store for `MEMBER_VERIFICATION_STATE`
                }
            }
        
            store.dispatch( updateMemberVerificationState( null ) );

             // mandatory and optional criteria fields
             const criteriaFields = get( data, 'criteria', [] );
             const mandatoryCriteria = get( data, 'mandatoryCriteria', {} );
             const optionalCriteria = get( data, 'optionalCriteria', {} );
             const minOptionalCriteriaMatch = get( data, 'minOptionalCriteriaMatch', null );
             const mandatoryFields = filter( criteriaFields, criteriaField => includes( mandatoryCriteria.ids, criteriaField.name ) );
             const optionalFields = filter( criteriaFields, criteriaField => includes( optionalCriteria.ids, criteriaField.name ) );

             // check if mandatory and optional fields are satisfied
             const isMandatorySectionSatisfied = every( mandatoryFields, mandatoryField => {
                 return get( fieldsStatus, `${ mandatoryField.name }.result`, false );
             } );
             const isOptionalSectionSatisfied = minOptionalCriteriaMatch <= reduce( optionalFields, ( sum, optionalField ) => {
                 return get( fieldsStatus, `${ optionalField.name }.result` ) ? sum + 1 : sum;
             }, 0 );
           

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                fieldsStatus,
                criteriaStatus: {
                    total: isMandatorySectionSatisfied && isOptionalSectionSatisfied,
                    mandatory: isMandatorySectionSatisfied,
                    optional: isOptionalSectionSatisfied,
                }
            }, ()  => {

                // execute callback to notify new verification state
                this.props.onMarkVerified( {
                    isMemberVerified: this.state.criteriaStatus.total
                } );

            } );

            
        }
    }

    /**
     * @desc Set `true` or `false` selection for a criteria field.
     */
    handleSetChoice( { field, result, value } = {} ) {
        log.debug( 'AuthFlowMemberVerificationMatchCriteriaWidget.handleSetChoice()' );

        // get field status from the state
        const fieldsStatus = cloneDeep( this.state.fieldsStatus );
        const fieldStatus = fieldsStatus[ field.name ];

        // update `result` value
        set( fieldStatus, 'result', result );

        // set `value` if the result is `true`
        set( fieldStatus, 'value', ( true === result ? value : null ) );

        /*-------*/
        
        // mandatory and optional criteria fields
        const criteriaFields = get( this.state.componentData, 'criteria', [] );
        const mandatoryCriteria = get( this.state.componentData, 'mandatoryCriteria', {} );
        const optionalCriteria = get( this.state.componentData, 'optionalCriteria', {} );
        const minOptionalCriteriaMatch = get( this.state.componentData, 'minOptionalCriteriaMatch', null );
        const mandatoryFields = filter( criteriaFields, criteriaField => includes( mandatoryCriteria.ids, criteriaField.name ) );
        const optionalFields = filter( criteriaFields, criteriaField => includes( optionalCriteria.ids, criteriaField.name ) );

        // check if mandatory and optional fields are satisfied
        const isMandatorySectionSatisfied = every( mandatoryFields, mandatoryField => {
            return get( fieldsStatus, `${ mandatoryField.name }.result`, false );
        } );
        const isOptionalSectionSatisfied = minOptionalCriteriaMatch <= reduce( optionalFields, ( sum, optionalField ) => {
            return get( fieldsStatus, `${ optionalField.name }.result` ) ? sum + 1 : sum;
        }, 0 );

        /*-------*/

        // update state
        this.setState( {
            fieldsStatus,
            criteriaStatus: {
                total: isMandatorySectionSatisfied && isOptionalSectionSatisfied,
                mandatory: isMandatorySectionSatisfied,
                optional: isOptionalSectionSatisfied,
            }
        }, () => {
            this.saveCriteriaSelection(); // update backend

            // execute callback to notify new verification state
            this.props.onMarkVerified( {
                isMemberVerified: this.state.criteriaStatus.total,
            } );
        } );
    }

    /**
     * @desc Update criteria selection on the backend.
     */
    async saveCriteriaSelection() {
        log.debug( 'AuthFlowMemberVerificationMatchCriteriaWidget.saveCriteriaSelection()' );

        // get save action (no-op if action is missing)
        const saveAction = find( get( this.state.componentData, 'actions', [] ), { name: 'save' } );

        if( isEmpty( saveAction ) ) {
            return;
        }

        /*----------*/

        // cancel ongoing request
        this.authFlowMemberVerificationMatchCriteriaService.cancel();

        // show page level preloader
        pageLevelPreloader.show( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // get status and service data
        const { status } = await this.authFlowMemberVerificationMatchCriteriaService.saveCriteriaSelection( {
            url: saveAction.url,
            data: {
                data: this.state.fieldsStatus,
                context: {
                    memberId: this.props.memberId,
                    patientId: this.props.patientId
                },
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
            return null; // ignore on request cancellations
         } else if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * handle action click
     */
    handleActionClick( { action, item } ) {
        log.debug( 'AuthFlowMemberVerificationMatchCriteriaWidget.handleActionClick()' );

        if( 'enrollToEpost' === action.name ) {

            const memberVerificationState = {
                memberId: this.props.memberId,
                patientId: this.props.patientId,
                fieldsStatus: get( this.state, 'fieldsStatus', {} )
            };

            store.dispatch( updateMemberVerificationState(  memberVerificationState ) );

            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.ENROLL_TO_EPOST,
                search: queryString.stringify( {
                    origin: 'verification'
                }  ),
                state: { memberId: item.memberId,  patientId: this.props.patientId }
            } );
        }
        
    }

    /*********************************************/

    /**
     * @desc When `memberId` changes in the props, referch the criteria data.
     */
    UNSAFE_componentWillReceiveProps( nextProps ) {
        log.debug( 'AuthFlowMemberVerificationMatchCriteriaWidget.componentWillReceiveProps()' );

        if( !isEqual( this.props.memberId, nextProps.memberId ) ) {
            this.setState( {
                isFetching: true,
                networkStatus: undefined,
                criteriaStatus: {
                    total: false,
                    mandatory: false,
                    optional: false,
                },
            }, () => {
                this.fetchComponentData();
                
                // execute callback to notify new verification state
                this.props.onMarkVerified( {
                    isMemberVerified: this.state.criteriaStatus.total,
                } );
             } );
        }
    }

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.authFlowMemberVerificationMatchCriteriaService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <AuthFlowMemberVerificationMatchCriteriaView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                memberId={ this.props.memberId }
                patientId={ this.props.patientId }
                { ...get( this.state, 'componentData', {} ) }
                fieldsStatus={ this.state.fieldsStatus }
                criteriaStatus={ this.state.criteriaStatus }
                verified={ this.state.criteriaStatus.total }
                onSetChoice={ this.handleSetChoice }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_AuthFlowMemberVerificationMatchCriteriaWidget.displayName = 'AuthFlowMemberVerificationMatchCriteriaWidget';

// set default props
_AuthFlowMemberVerificationMatchCriteriaWidget.defaultProps = {
    onMarkVerified: noop,
    verified: false,
};

// prop types
_AuthFlowMemberVerificationMatchCriteriaWidget.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),

        // updateMemberVerificationState: bin
    };
};

// return component with acccess to Redux store
export const AuthFlowMemberVerificationMatchCriteriaWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowMemberVerificationMatchCriteriaWidget ) );

